import React, { useEffect, useRef, useState } from "react";
import { Scissors, Image as ImageIcon, Camera, Upload } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}${s < 60 ? `.${Math.floor((s % 1) * 10)}` : ""}`;

/**
 * Trim + cover editor. Emits { trim_start, trim_end, cover_time, cover_url? } via onChange.
 * Preview loops inside the selected range.
 */
export default function ReelTrimEditor({ videoUrl, value, onChange }) {
  const vid = useRef(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [mode, setMode] = useState("trim"); // trim | cover
  const { trim_start = 0, trim_end = null, cover_time = null, cover_url = null } = value || {};
  const end = trim_end ?? duration;

  useEffect(() => {
    const v = vid.current;
    if (!v) return;
    const onMeta = () => { setDuration(v.duration || 0); if (!trim_end) onChange({ ...value, trim_start: 0, trim_end: v.duration, cover_time: value?.cover_time ?? 0, _duration: v.duration }); };
    const onTime = () => {
      setCurrent(v.currentTime);
      if (mode === "trim" && (v.currentTime >= end - 0.05 || v.currentTime < trim_start - 0.2)) v.currentTime = trim_start;
    };
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    return () => { v.removeEventListener("loadedmetadata", onMeta); v.removeEventListener("timeupdate", onTime); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, trim_start, end, mode]);

  const seek = (t) => { const v = vid.current; if (v) { v.currentTime = t; setCurrent(t); } };
  const setStart = (t) => { const s = Math.min(Math.max(0, t), end - 0.5); onChange({ ...value, trim_start: s }); seek(s); };
  const setEnd = (t) => { const e = Math.max(Math.min(duration, t), trim_start + 0.5); onChange({ ...value, trim_end: e }); seek(Math.max(trim_start, e - 0.3)); };
  const pickCover = (t) => { onChange({ ...value, cover_time: t, cover_url: null }); seek(t); };

  const pct = (t) => (duration ? `${(t / duration) * 100}%` : "0%");

  return (
    <div className="space-y-3" data-testid="reel-trim-editor">
      <div className="relative aspect-[9/16] max-h-[360px] mx-auto rounded-2xl bg-black overflow-hidden border border-border">
        <video ref={vid} src={videoUrl} className="w-full h-full object-contain" autoPlay muted loop playsInline data-testid="reel-trim-video" />
        {mode === "cover" && cover_url && <img src={cover_url} alt="capa" className="absolute inset-0 w-full h-full object-cover" data-testid="reel-cover-preview" />}
        <div className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/60 text-white">{mode === "trim" ? `Corte: ${fmt(trim_start)} → ${fmt(end)} (${fmt(end - trim_start)})` : `Capa em ${fmt(cover_time ?? 0)}`}</div>
      </div>

      <div className="inline-flex rounded-full bg-muted p-0.5 text-xs">
        <button type="button" onClick={() => setMode("trim")} data-testid="reel-mode-trim" className={`px-3 py-1 rounded-full flex items-center gap-1 ${mode === "trim" ? "bg-background shadow font-semibold" : ""}`}><Scissors className="w-3 h-3" /> Cortar</button>
        <button type="button" onClick={() => { setMode("cover"); vid.current?.pause(); seek(cover_time ?? trim_start); }} data-testid="reel-mode-cover" className={`px-3 py-1 rounded-full flex items-center gap-1 ${mode === "cover" ? "bg-background shadow font-semibold" : ""}`}><ImageIcon className="w-3 h-3" /> Capa</button>
      </div>

      {mode === "trim" ? (
        <div className="space-y-2">
          <div className="relative h-10 rounded-lg bg-muted overflow-hidden select-none" data-testid="reel-trim-timeline">
            <div className="absolute inset-y-0 bg-primary/25 border-x-2 border-primary" style={{ left: pct(trim_start), width: pct(end - trim_start) }} />
            <div className="absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: pct(current) }} />
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <label className="space-y-1">
              <span className="text-muted-foreground">Início: <b>{fmt(trim_start)}</b></span>
              <input type="range" min={0} max={duration || 0} step={0.1} value={trim_start} onChange={(e) => setStart(parseFloat(e.target.value))} className="w-full accent-primary" data-testid="reel-trim-start" />
            </label>
            <label className="space-y-1">
              <span className="text-muted-foreground">Fim: <b>{fmt(end)}</b></span>
              <input type="range" min={0} max={duration || 0} step={0.1} value={end} onChange={(e) => setEnd(parseFloat(e.target.value))} className="w-full accent-primary" data-testid="reel-trim-end" />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">Duração total do vídeo: {fmt(duration)}. O trecho fora do corte será removido ao publicar.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-xs space-y-1">
            <span className="text-muted-foreground flex items-center gap-1"><Camera className="w-3 h-3" /> Arraste para escolher o quadro da capa</span>
            <input type="range" min={trim_start} max={end || 0} step={0.1} value={Math.min(Math.max(cover_time ?? trim_start, trim_start), end || 0)} onChange={(e) => pickCover(parseFloat(e.target.value))} className="w-full accent-primary" data-testid="reel-cover-time" />
          </label>
          <div className="flex items-center gap-2">
            <ImageUploader testid="reel-cover-upload" label={cover_url ? "Trocar imagem da capa" : "Enviar imagem própria"} variant="outline" onUploaded={(url) => onChange({ ...value, cover_url: url })} />
            {cover_url && <button type="button" onClick={() => onChange({ ...value, cover_url: null })} className="text-xs text-muted-foreground hover:underline" data-testid="reel-cover-use-frame">Usar quadro do vídeo</button>}
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Upload className="w-3 h-3" /> {cover_url ? "Capa personalizada selecionada." : "A capa será gerada a partir do quadro escolhido."}</p>
        </div>
      )}
    </div>
  );
}
